import React, {useEffect, useRef, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import { api } from "../../api";
import {
    addFacetValue,
    addExcludedFacetValue,
    removeExcludedFacetValue,
    removeFacetValue,
    searchReferences,
    setSearchFacetsLimits,
    setSearchResultsPage,
    setAuthorFilter,
    filterFacets,
    setDatePubmedAdded,
    setDatePubmedModified,
    setDatePublished,
    setDateCreated,
    setConfidenceScore,
    setApplyToSingleTag,
    setSearchMode,
    removeDatePubmedAdded,
    removeDatePubmedModified,
    removeDatePublished,
    removeDateCreated
} from '../../actions/searchActions';
import Form from 'react-bootstrap/Form';
import {Badge, Button, Collapse, ButtonGroup, OverlayTrigger, Tooltip} from 'react-bootstrap';
import {IoIosArrowDroprightCircle, IoIosArrowDropdownCircle} from 'react-icons/io';
import {INITIAL_FACETS_LIMIT} from '../../reducers/searchReducer';
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import InputGroup from 'react-bootstrap/InputGroup';
import _ from "lodash";
import LoadingOverlay from "../LoadingOverlay";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheckSquare, faMinusSquare} from "@fortawesome/free-solid-svg-icons";
import sgdIcon from '../../images/sgd_icon.png';
import mgiIcon from '../../images/mgi_icon.png';
import rgdIcon from '../../images/rgd_icon.png';
import wbIcon from '../../images/wb_icon.png';
import fbIcon from '../../images/fb_icon.png';
import xbIcon from '../../images/xb_icon.png';
import zfinIcon from '../../images/zfin_icon.png';
import {Slider} from "@mui/material";

const MOD_ICONS = {
  SGD: sgdIcon,
  MGI: mgiIcon,
  RGD: rgdIcon,
  WB: wbIcon,
  FB: fbIcon,
  XB: xbIcon,
  ZFIN: zfinIcon,
};


export const RENAME_FACETS = {
    "category.keyword": "alliance category",
    "mods_in_corpus.keyword": "corpus - in corpus",
    "mods_needs_review.keyword": "corpus - needs review",
    "mods_in_corpus_or_needs_review.keyword": "corpus - in corpus or needs review",
    "authors.name.keyword": "Authors",
    "mod_reference_types.keyword": "MOD reference type",
    "retraction_status.keyword": "Retraction Status",
    "can_display_image": "Image permission",
    "has_image": "Has images",
    "topics": "Topic",
    "confidence_levels": "Confidence level",
    "source_methods": "Source method",
    "source_evidence_assertions": "Source evidence assertion",
    "data_novelty": "Data Novelty",
    "large_scale_tag": "Large-scale study",
    "file_workflow": "File workflow",
    "reference_classification": "Reference classification",
    "entity_extraction": "Entity extraction",
    "manual_indexing": "Manual indexing",
    "curation_classification": "Curation classification",
    "community_curation": "Community curation",
    "first_pass_curation": "First pass curation",
    "email_extraction": "Email extraction",
    "predicted_indexing_priority": "Predicted indexing priority",
    "indexing_priority": "Indexing priority",
    "manual_indexing_curation_tag": "Manual indexing curation tag",
    "datePubmedAdded": {
        label: "Date Range: Added to PubMed",
        value: (state) => state.search.datePubmedAdded,
        action: removeDatePubmedAdded
    },
    "datePubmedModified": {
        label: "Date Range: Modified in PubMed",
        value: (state) => state.search.datePubmedModified,
        action: removeDatePubmedModified
    },
    "datePublished": {
        label: "Date Range: Published",
        value: (state) => state.search.datePublished,
        action: removeDatePublished
    },
    "dateCreated": {
        label: "Date Range: Added to ABC",
        value: (state) => state.search.dateCreated,
        action: removeDateCreated
    },
    "confidence_scores": "Confidence Score"
}


// Per-facet relabeling of individual bucket values. Keyed by the resolved
// facet key, then by the raw bucket value (bucket.key / bucket.name).
// "no genetic data" in the manual indexing curation tag facet comes from the
// predicted curation tag column, so display it as "predicted no genetic data"
// to make clear it is a computed prediction, not a curator-added tag.
export const RENAME_FACET_VALUES = {
    "manual_indexing_curation_tag": {
        "no genetic data": "predicted no genetic data"
    },
    "can_display_image": {
        "true": "Yes",
        "false": "No"
    },
    "has_image": {
        "true": "Yes",
        "false": "No"
    },
    "large_scale_tag": {
        "true": "Yes"
    }
}


export const FACETS_CATEGORIES_WITH_FACETS = {
    "Alliance Metadata": ["mods in corpus", "mods needs review", "mods in corpus or needs review"],
    "Workflow Tags": ["file_workflow", "reference_classification", "entity_extraction", "manual_indexing", "curation_classification", "community_curation", "first_pass_curation", "email_extraction"],
    "Curation Classification Tags": ["predicted_indexing_priority", "indexing_priority", "manual_indexing_curation_tag"],
    "Bibliographic Data": ["mod reference types", "pubmed types", "category", "pubmed publication status", "retraction status", "authors.name", "language"],
    "Images": ["can display image", "has image"],
    "Topics and Entities": ["topics", "confidence_levels", "confidence_scores", "source_methods", "source_evidence_assertions", "data_novelty", "large_scale_tag"],
    "Date Range": ["Date Modified in Pubmed", "Date Added To Pubmed", "Date Published", "Date Added to ABC"]
}

// Only commit years the backend can actually search. Two failure modes
// otherwise: (1) a native date input holds intermediate values while the
// year is being typed ("2026" passes through "0026"), and (2) the year
// segment accepts 5-6 digit years regardless of the input's min/max.
// date_created is compared in Elasticsearch as an epoch long in
// nanoseconds, which only represents 1677-09-21..2262-04-11 — anything
// outside 500s with 'long overflow' / 'Unable to obtain Instant'. These
// whole-year bounds sit safely inside that and cover all real dates.
const MIN_SEARCHABLE_YEAR = 1678;
const MAX_SEARCHABLE_YEAR = 2261;
const MIN_SEARCHABLE_DATE = `${MIN_SEARCHABLE_YEAR}-01-01`;
const MAX_SEARCHABLE_DATE = `${MAX_SEARCHABLE_YEAR}-12-31`;
function hasSearchableYear(dateStr){
    const year = parseInt(dateStr.split('-')[0], 10);
    return year >= MIN_SEARCHABLE_YEAR && year <= MAX_SEARCHABLE_YEAR;
}

const DatePicker = ({facetName,currentValue,setValueFunction}) => {
    const dispatch = useDispatch();

    // Local state backing the two native <input type="date"> fields so the
    // curator can type freely; the range is committed on Enter/blur, or
    // shortly after a change settles (a calendar pick never blurs the input).
    const [startInput, setStartInput] = useState('');
    const [endInput, setEndInput] = useState('');
    // Set when a commit was actually attempted (blur/Enter/settle timer) and
    // declined, flagging the field(s) at fault — not derived from render
    // state, so half-built ranges and mid-typed years don't flash red while
    // the curator is still editing.
    const [declinedStart, setDeclinedStart] = useState(false);
    const [declinedEnd, setDeclinedEnd] = useState(false);
    const commitTimerRef = useRef(null);

    function clearPendingCommit(){
        if (commitTimerRef.current){
            clearTimeout(commitTimerRef.current);
            commitTimerRef.current = null;
        }
    }

    // Cancel a scheduled commit if the facet unmounts (e.g. panel collapsed).
    useEffect(() => clearPendingCommit, []);

    // Keep the typed inputs in sync with the stored range so the Day/Week/
    // Month/Year buttons and the clear (x) button are reflected here too.
    // Anything landing here reflects a successful commit or an external
    // change, so any declined flags are moot.
    useEffect(() => {
        setDeclinedStart(false);
        setDeclinedEnd(false);
        if (Array.isArray(currentValue) && currentValue.length === 2){
            setStartInput(currentValue[0] || '');
            setEndInput(currentValue[1] || '');
        } else {
            setStartInput('');
            setEndInput('');
        }
    }, [currentValue]);


    function commitTypedRange(startStr, endStr){
        clearPendingCommit();
        // Both cleared -> remove the filter entirely (unless there is none).
        if (!startStr && !endStr){
            setDeclinedStart(false);
            setDeclinedEnd(false);
            if (currentValue !== ''){
                dispatch(setValueFunction(''));
                dispatch(setSearchResultsPage(1));
                dispatch(searchReferences());
            }
            return;
        }
        // Need both ends present, valid, with searchable years, and in order
        // to form a range; otherwise decline and flag the field(s) at fault.
        // An out-of-order range means the curator is mid-edit (e.g. moved the
        // start forward before touching the end, when tabbing between the
        // boxes commits on blur) — swapping and committing it would let the
        // currentValue sync effect rewrite the boxes with values never typed
        // (YYYY-MM-DD compares lexicographically).
        const startOk = Boolean(startStr) && !isNaN(Date.parse(startStr)) && hasSearchableYear(startStr);
        const endOk = Boolean(endStr) && !isNaN(Date.parse(endStr)) && hasSearchableYear(endStr);
        const outOfOrder = startOk && endOk && startStr > endStr;
        if (!startOk || !endOk || outOfOrder){
            setDeclinedStart(!startOk || outOfOrder);
            setDeclinedEnd(!endOk || outOfOrder);
            return;
        }
        setDeclinedStart(false);
        setDeclinedEnd(false);
        // Skip the no-op re-search when the committed range is unchanged
        // (e.g. blur right after the settle timer already committed).
        if (Array.isArray(currentValue) && currentValue[0] === startStr && currentValue[1] === endStr){
            return;
        }
        dispatch(setValueFunction([startStr, endStr]));
        dispatch(setSearchResultsPage(1));
        dispatch(searchReferences());
    }

    // A calendar pick fires onChange but keeps focus in the input, so nothing
    // would blur and commit the range. Schedule a commit once the value stops
    // changing; the delay also absorbs segment-by-segment keyboard edits, and
    // the plausible-year guard holds back mid-typed years that survive it.
    function handleDateInputChange(nextStart, nextEnd){
        setStartInput(nextStart);
        setEndInput(nextEnd);
        // The curator is editing again — withdraw any declined flags until
        // the next commit attempt (settle timer, blur, or Enter) re-judges.
        setDeclinedStart(false);
        setDeclinedEnd(false);
        clearPendingCommit();
        commitTimerRef.current = setTimeout(() => {
            commitTimerRef.current = null;
            commitTypedRange(nextStart, nextEnd);
        }, 800);
    }

    function handleInputKeyDown(e){
        if (e.key === 'Enter'){
            e.preventDefault();
            e.target.blur(); // blur handler performs the commit
        }
    }

    function formatDateRange(dateRange){
            let dateStart=dateRange[0].getFullYear()+"-"+parseInt(dateRange[0].getMonth()+1).toString().padStart(2,'0')+"-"+dateRange[0].getDate().toString().padStart(2,'0');
            let dateEnd=dateRange[1].getFullYear()+"-"+parseInt(dateRange[1].getMonth()+1).toString().padStart(2,'0')+"-"+dateRange[1].getDate().toString().padStart(2,'0');
            return [dateStart,dateEnd];
    }

    function handleFixedTimeClick(timeframe){
        var today = new Date();
        let newDate = ['',''];
        if(timeframe === 'Day'){
            newDate = formatDateRange([today,today]);
        }
        else if(timeframe === 'Week'){
            let lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            newDate = formatDateRange([lastWeek,today]);
        }
        else if (timeframe === 'Month'){
            let lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
            newDate = formatDateRange([lastMonth,today]);
        }
        else if(timeframe === 'Year'){
            let lastYear = new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000);
            newDate = formatDateRange([lastYear,today]);
        }
        dispatch(setValueFunction(newDate));
        dispatch(searchReferences());
    }

    return(
        <div key={facetName} style={{textAlign: "left", paddingLeft: "2em", paddingBottom: "0.5em"}}>
            <h5>{facetName}</h5>
            <ButtonGroup aria-label="DateSetter" size ="sm" style={{display: "block"}}>
                <Button variant="secondary" style={{'borderBottomLeftRadius' : 0}} onClick={() => {handleFixedTimeClick('Day')}}>Day</Button>
                <Button variant="secondary" onClick={() => {handleFixedTimeClick('Week')}}>Week</Button>
                <Button variant="secondary" onClick={() => {handleFixedTimeClick('Month')}}>Month</Button>
                <Button variant="secondary" style={{'borderBottomRightRadius' : 0}} onClick={() => {handleFixedTimeClick('Year')}}>Year</Button>
            </ButtonGroup>
            {/* "from" and "to" stacked on their own rows: the facet sidebar is
                too narrow for both date inputs side by side (curator request). */}
            <div style={{marginTop: "0.4em"}}>
                <InputGroup size="sm">
                    <InputGroup.Text style={{width: "3.2em", justifyContent: "center"}}>from</InputGroup.Text>
                    <Form.Control
                        type="date"
                        aria-label={`${facetName} start date`}
                        value={startInput}
                        className="date-facet-input"
                        isInvalid={declinedStart}
                        min={MIN_SEARCHABLE_DATE}
                        max={MAX_SEARCHABLE_DATE}
                        onChange={(e) => handleDateInputChange(e.target.value, endInput)}
                        onBlur={() => commitTypedRange(startInput, endInput)}
                        onKeyDown={handleInputKeyDown}
                    />
                </InputGroup>
                <InputGroup size="sm" style={{marginTop: "0.25em"}}>
                    <InputGroup.Text style={{width: "3.2em", justifyContent: "center"}}>to</InputGroup.Text>
                    <Form.Control
                        type="date"
                        aria-label={`${facetName} end date`}
                        value={endInput}
                        className="date-facet-input"
                        isInvalid={declinedEnd}
                        min={MIN_SEARCHABLE_DATE}
                        max={MAX_SEARCHABLE_DATE}
                        onChange={(e) => handleDateInputChange(startInput, e.target.value)}
                        onBlur={() => commitTypedRange(startInput, endInput)}
                        onKeyDown={handleInputKeyDown}
                    />
                    <Button
                        variant="outline-secondary"
                        aria-label={`Clear ${facetName} date range`}
                        title="Clear dates"
                        onClick={() => {
                            setStartInput('');
                            setEndInput('');
                            commitTypedRange('', '');
                        }}
                    >
                        &times;
                    </Button>
                </InputGroup>
            </div>
        </div>
    )
}
const DateFacet = ({facetsToInclude}) => {

  const datePubmedModified = useSelector(state => state.search.datePubmedModified);
  const datePubmedAdded = useSelector(state => state.search.datePubmedAdded);
  const datePublished = useSelector(state => state.search.datePublished);
  const dateCreated = useSelector(state => state.search.dateCreated);

  return (
        <div>
            <Container>
                <Row>
                    <Col>
                        <DatePicker facetName={facetsToInclude[2]} currentValue={datePublished} setValueFunction={setDatePublished}/>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <DatePicker facetName={facetsToInclude[0]} currentValue={datePubmedModified} setValueFunction={setDatePubmedModified}/>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <DatePicker facetName={facetsToInclude[1]} currentValue={datePubmedAdded} setValueFunction={setDatePubmedAdded}/>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <DatePicker facetName={facetsToInclude[3]} currentValue={dateCreated} setValueFunction={setDateCreated}/>
                    </Col>
                </Row>
            </Container>
        </div>
  )
}

const Facet = ({facetsToInclude, renameFacets}) => {
    const searchFacets = useSelector(state => state.search.searchFacets);
    const searchFacetsValues = useSelector(state => state.search.searchFacetsValues);
    const searchExcludedFacetsValues = useSelector(state => state.search.searchExcludedFacetsValues);
    const dispatch = useDispatch();
    const negatedFacetCategories = ["pubmed publication status", "mod reference types", "category", "pubmed types", "confidence_levels", "retraction status", "source_methods", "source_evidence_assertions"];
    const [openSubFacets, setOpenSubFacets] = useState(new Set());

    const [sourceMethodDescriptions, setSourceMethodDescriptions] = useState({});
    const [sourceEvidenceAssertionDescriptions, setSourceEvidenceAssertionDescriptions] = useState({});
    const Child_terms = ["eco:0008004","eco:0008021","atp:0000035","atp:0000036"];

    const [modRefTypeToMods, setModRefTypeToMods] = useState({});

    useEffect(() => {
      const needs = facetsToInclude.includes('mod reference types') || facetsToInclude.includes('mod_reference_types');
      if (!needs) return;


      (async () => {
        try {
          const response = await api.get('/reference/mod_reference_type/utils/mod_reftype_to_mods');
          setModRefTypeToMods(response.data || {});
        } catch (e) {
          console.log("Failed to load mod-ref-type map:", e);
        }
      })();
    }, [facetsToInclude]);

    // fetch source method descriptions if 'source_methods' is included
    useEffect(() => {
        if (facetsToInclude.includes('source_methods')) {
            api.get('/tag_source/all')
                .then(response => {
                    const data = response.data;
                    const mapping = {};
                    if (Array.isArray(data)) {
                        data.forEach(item => {
                            mapping[item.source_method.toLowerCase()] = item.description;
                        });
                    }
                    setSourceMethodDescriptions(mapping);
                })
                .catch(err => console.error("Error fetching source method descriptions:", err));
        }
    }, [facetsToInclude]);

    useEffect(() => {
      if (
        facetsToInclude.includes('source_evidence_assertions') &&
        searchFacets['source_evidence_assertions'] &&
        searchFacets['source_evidence_assertions'].buckets
      ) {
        const fetchData = async () => {
            const buckets = searchFacets['source_evidence_assertions'].buckets;

            const mapping = {};

            for (const bucket of buckets) {
                const upperKey = bucket.key.toUpperCase();
                let url = '';

                // --- Use AGR REST API ---
                if (upperKey.startsWith('ATP')) {
                    url = `/ontology/map_curie_to_name/atpterm/${upperKey}`;
                } else if (upperKey.startsWith('ECO')) {
                    url = `/ontology/map_curie_to_name/ecoterm/${upperKey}`;
                } else {
                    console.warn(`Unknown prefix in bucket.key: ${bucket.key}`);
                    continue;
                }

                try {
                    const result = await api.get(url);
                    // This endpoint returns the name string directly
                    mapping[bucket.key] = result.data;

                } catch (error) {
                    console.error("Error fetching CURIE → name mapping:", error);
                }
            }

            setSourceEvidenceAssertionDescriptions(mapping);
        };

        fetchData();
      }
    }, [facetsToInclude, searchFacets]);

    const toggleSubFacet = (subFacetLabel) => {
        const newOpenSubFacets = new Set([...openSubFacets]);
        newOpenSubFacets.has(subFacetLabel) ?
            newOpenSubFacets.delete(subFacetLabel) :
            newOpenSubFacets.add(subFacetLabel);
        setOpenSubFacets(newOpenSubFacets);
    };

    const StandardFacetCheckbox = ({facet, value}) => {
        return(

                <Form.Check inline type="checkbox"
                    checked={searchFacetsValues.hasOwnProperty(facet) && searchFacetsValues[facet].includes(value)}
                    onChange={(evt) => {
                        if (evt.target.checked) {
                            dispatch(addFacetValue(facet, value));
                            if(facet === 'topics'  && !searchExcludedFacetsValues.confidence_levels  && !searchFacetsValues.confidence_levels) {
                                dispatch(addExcludedFacetValue('confidence_levels', 'NEG'));
                            }
                        } else {
                            //Remove NOT Confidence Levels if no topic is selected.
                            if(facet === 'topics' && searchExcludedFacetsValues.confidence_levels){
                                console.log(searchFacetsValues.topics);
                                searchExcludedFacetsValues.confidence_levels.forEach(confidence_level => {
                                    dispatch(removeExcludedFacetValue('confidence_levels', confidence_level))
                                })
                            }
                            dispatch(removeFacetValue(facet, value));
                        }
                }}/>
        )
    }

    const NegatedFacetCheckbox = ({facet, value}) => {
        const includedChecked= searchFacetsValues.hasOwnProperty(facet) && searchFacetsValues[facet].includes(value);
        const excludedChecked= searchExcludedFacetsValues.hasOwnProperty(facet) && searchExcludedFacetsValues[facet].includes(value);

        const handleClickInclude = () => {
            if (!includedChecked && excludedChecked) {
                dispatch(removeExcludedFacetValue(facet, value));
                dispatch(addFacetValue(facet, value));
            } else if(!includedChecked){
                dispatch(addFacetValue(facet, value));
            }
            else {
                dispatch(removeFacetValue(facet, value));
            }
        }
        const handleClickExclude = () => {
            if (!excludedChecked && includedChecked) {
                dispatch(removeFacetValue(facet, value));
                dispatch(addExcludedFacetValue(facet, value));
            } else if (!excludedChecked){
                dispatch(addExcludedFacetValue(facet, value));
            } else {
                dispatch(removeExcludedFacetValue(facet, value));
            }
        }

        return(
            <span>
                <FontAwesomeIcon icon={faCheckSquare} style= {includedChecked ? {color: "#28a745"} : {color: "#808080"}}
                    onClick={handleClickInclude}/>
                &nbsp;
                <FontAwesomeIcon icon={faMinusSquare} style= {excludedChecked ? {color: "#dc3545"} : {color: "#808080"}}
                     onClick={handleClickExclude}/>
            </span>
        )
    }

    const renderModIcons = (refTypeLabel /* string from bucket.key */) => {
      const mods = modRefTypeToMods?.[refTypeLabel] || [];
      if (!mods.length) return null;

      return (
        <span style={{ marginRight: 6, display: 'inline-flex', gap: 4, verticalAlign: 'middle' }}>
          {mods.map((mod) => {
            const src = MOD_ICONS[mod];
            if (!src) return null; 
            return (
              <img
                key={`${refTypeLabel}-${mod}`}
                src={src}
                alt={mod}
                width={20}
                height={20}
                style={{ imageRendering: 'crisp-edges', verticalAlign: 'text-bottom' }}
                title={mod}
              />
            );
          })}
        </span>
      );
    };

    return (
        <div className="facet-container">
            {facetsToInclude.map(facetToInclude => {
                let key = facetToInclude.replaceAll(' ', '_');
                if (!['topics', 'confidence_levels', 'confidence_scores', 'source_methods', 'source_evidence_assertions', 'data_novelty', 'large_scale_tag',
                        'file_workflow', 'manual_indexing', 'reference_classification',
		                'entity_extraction', 'curation_classification', 'community_curation', 'first_pass_curation', 'email_extraction',
                        'predicted_indexing_priority', 'indexing_priority', 'manual_indexing_curation_tag',
                        'can_display_image', 'has_image'].includes(key)) {
                    key = key + '.keyword';
                }

                if (!searchFacets[key]?.buckets?.length) return null;

                const displayName = renameFacets[key] || key.replace(/(\.keyword|_)/g, ' ');

                const isOpen = openSubFacets.has(facetToInclude);

		// --- detect the specific facet for mod reference types ---
		const isModRefTypeFacet = (key === 'mod_reference_types.keyword');

                return (
                    <div key={facetToInclude} style={{ marginLeft: '15px', marginBottom: '5px' }}>
                        <Button
                            variant="light"
                            size="md"
                            onClick={() => toggleSubFacet(facetToInclude)}
                            style={{ 
                                textAlign: 'left',
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '1rem' // slightly smaller than main categories
                            }}
                        >
                            {isOpen ? 
                                <IoIosArrowDropdownCircle style={{ marginRight: '8px', fontSize: '1.2rem' }} /> : 
                                <IoIosArrowDroprightCircle style={{ marginRight: '8px', fontSize: '1.2rem' }} />
                            }
                            {displayName}
                        </Button>
                        
                        <Collapse in={isOpen}>
                            <div style={{ marginLeft: '20px' }}>
                                {facetToInclude === 'authors.name' && <AuthorFilter />}
                                {facetToInclude === 'confidence_scores' && <NumberRangeWidget/>}
                                {facetToInclude !== 'confidence_scores' && searchFacets[key].buckets.map(bucket => {
                                    const valueRenames = RENAME_FACET_VALUES[key];
                                    const bucketLabel = (valueRenames && (valueRenames[bucket.key] || valueRenames[bucket.name])) || bucket.name || bucket.key;
                                    return (
                                    <Container key={bucket.key}>
                                        <Row className="facet-item">

                                            <Col xs={3} sm={2} className={Child_terms.includes(bucket.key) ? 'facet-indent' : null}>
                                                {negatedFacetCategories.includes(facetToInclude) ? 
                                                    <NegatedFacetCheckbox facet={key} value={bucket.key} /> : 
                                                    <StandardFacetCheckbox facet={key} value={bucket.key} />
                                                }
                                            </Col>

                                            <Col xs={6} sm={7} className={Child_terms.includes(bucket.key) ? 'facet-indent-text' : null}>
                                                {(key === 'source_methods' || key === 'source_evidence_assertions') ? (

                                                    <OverlayTrigger
                                                        placement="right"
                                                        overlay={
                                                            <Tooltip id={`tooltip-${bucket.key}`}>                                                                                                                    {(key === 'source_methods'
                                                                    ? sourceMethodDescriptions[bucket.key]
                                                                    : sourceEvidenceAssertionDescriptions[bucket.key]) || 'No description available.'}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span dangerouslySetInnerHTML={{ __html: bucketLabel }} />
                                                    </OverlayTrigger>
                                                ) : (
                                                    <span dangerouslySetInnerHTML={{ __html: bucketLabel }} />
                                                )}                                                                                                                                                {isModRefTypeFacet && renderModIcons(bucket.key)}
                                            </Col>                                                                                                                    
                                            <Col xs={3} sm={3}>                                                                                                       
                                                <Badge variant="secondary">                                                                                           
                                                    {['topics', 'confidence_levels', 'source_methods', 'source_evidence_assertions', 'data_novelty', 'large_scale_tag'].includes(key) &&
                                                        bucket.docs_count !== undefined ?
                                                        bucket.docs_count.doc_count.toLocaleString() :
                                                        bucket.doc_count.toLocaleString()}                                                                            
                                                </Badge>                                                                                                              
                                            </Col>                                                                                                                    
                                        </Row>
                                    </Container>
                                    );
                                })}
                                <ShowMoreLessAllButtons facetLabel={key} facetValue={searchFacets[key]} />                                                            
                            </div>                                                                                                                                    
                        </Collapse>                                                                                                                                   
                    </div>
                );
            })}
        </div>
    );
};
const NumberRangeWidget = () => {
    const confidenceScore = useSelector(state => state.search.confidenceScore);
    const dispatch = useDispatch();

    const handleChange = (event, newValue) => {
        dispatch(setConfidenceScore(newValue));
        dispatch(searchReferences());
    };

    return (
        <div className="number-range-widget">
            <Slider
                aria-label="Always visible"
                size="small"
                value ={confidenceScore}
                onChange={handleChange}
                min={0}
                max={1}
                step={0.05}
                valueLabelDisplay="on"
            />
        </div>
    )
}


const AuthorFilter = () => {
  const authorFilter = useSelector(state => state.search.authorFilter);
  const dispatch = useDispatch();

  return (
    <InputGroup size="sm" className="mb-3" style ={{width: "85%"}}>
    <Form.Control inline="true" type="text" id="authorFilter" name="authorFilter" placeholder="Filter Authors (case sensitive)" value={authorFilter}
                  onChange={(e) => dispatch(setAuthorFilter(e.target.value))}
                  onKeyPress={(event) => {
                      if (event.charCode === 13) {
                          dispatch(filterFacets())
                      }
                  }}
    />
    <Button inline="true" style={{width: "4em"}} size="sm"
      onClick={() => {
        dispatch(filterFacets())
      }}>Filter</Button>
      <Button variant="danger" size = "sm"
        onClick={() => {
          dispatch(setAuthorFilter(''));
          dispatch(filterFacets())
      }}>X</Button></InputGroup>
  )
}


const ShowMoreLessAllButtons = ({facetLabel, facetValue}) => {
    const searchFacetsLimits = useSelector(state => state.search.searchFacetsLimits);
    const dispatch = useDispatch();

    return (
        <div style={{paddingLeft: "1em"}}>
            {facetValue.buckets && facetValue.buckets.length >= searchFacetsLimits[facetLabel] ?
                <button className="button-to-link" onClick={()=> {
                    let newSearchFacetsLimits = _.cloneDeep(searchFacetsLimits);
                    newSearchFacetsLimits[facetLabel] = searchFacetsLimits[facetLabel] * 2;
                    dispatch(setSearchFacetsLimits(newSearchFacetsLimits));
                    dispatch(filterFacets());
                }}>+Show More</button> : null
            }
            {searchFacetsLimits[facetLabel] > INITIAL_FACETS_LIMIT ?
                <span>&nbsp;&nbsp;&nbsp;&nbsp;<button className="button-to-link" onClick={() => {
                    let newSearchFacetsLimits = _.cloneDeep(searchFacetsLimits);
                    newSearchFacetsLimits[facetLabel] = searchFacetsLimits[facetLabel] = INITIAL_FACETS_LIMIT;
                    dispatch(setSearchFacetsLimits(newSearchFacetsLimits));
                    dispatch(filterFacets());
                }}>-Show Less</button></span> : null
            }
            {facetValue.buckets && facetValue.buckets.length >= searchFacetsLimits[facetLabel] ? <span>&nbsp;&nbsp;&nbsp;&nbsp;
                <button className="button-to-link" onClick={() =>{
                    let newSearchFacetsLimits = _.cloneDeep(searchFacetsLimits);
                    newSearchFacetsLimits[facetLabel] = searchFacetsLimits[facetLabel] = 1000;
                    dispatch(setSearchFacetsLimits(newSearchFacetsLimits));
                    dispatch(filterFacets());
                }}>+Show All</button></span> : null }
            </div>
    )
}


const Facets = () => {

    const [openFacets, setOpenFacets] = useState(new Set());
    const searchFacetsValues = useSelector(state => state.search.searchFacetsValues);
    const searchExcludedFacetsValues = useSelector(state => state.search.searchExcludedFacetsValues);
    const readyToFacetSearch = useSelector(state => state.search.readyToFacetSearch);
    const facetsLoading = useSelector(state => state.search.facetsLoading);
    const datePubmedModified = useSelector(state => state.search.datePubmedModified);
    const datePubmedAdded = useSelector(state => state.search.datePubmedAdded);
    const datePublished= useSelector(state => state.search.datePublished);
    const dateCreated = useSelector(state => state.search.dateCreated);
    const applyToSingleTag = useSelector(state => state.search.applyToSingleTag);
    const seaValues = useSelector(state => state.search.searchFacetsValues['source_evidence_assertions'] || []);
    const hasGroupSEA = seaValues.some(v => v === 'ECO:0006155' || v === 'ECO:0007669');
    const searchMode = useSelector(state => state.search.searchMode);
    const dispatch = useDispatch();

    // Whenever a group-SEA is selected, turn off applyToSingleTag
    useEffect(() => {
      if (hasGroupSEA && applyToSingleTag) {
        dispatch(setApplyToSingleTag(false));
      }
    }, [hasGroupSEA, applyToSingleTag, dispatch]);

    const handleCheckboxChange = (event) => {
        dispatch(setApplyToSingleTag(event.target.checked));
    };
    
    const toggleFacetGroup = (facetGroupLabel) => {
        let newOpenFacets = new Set([...openFacets]);
        if (newOpenFacets.has(facetGroupLabel)) {
            newOpenFacets.delete(facetGroupLabel);
        } else {
            newOpenFacets.add(facetGroupLabel);
        }
        setOpenFacets(newOpenFacets);
    }

    // Handle facet/filter changes - only triggers search AFTER initial search is done
    // (Initial search is triggered by PersonSettingsControls after applying saved settings)
    useEffect(() => {
        // Skip if not ready for facet search (set to true after first successful search)
        if (!readyToFacetSearch) return;

        dispatch(setSearchResultsPage(1));
        dispatch(setAuthorFilter(""));
        dispatch(searchReferences());
    }, [searchFacetsValues, searchExcludedFacetsValues, applyToSingleTag]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-open facet categories based on selected filters
    useEffect(() => {
      const ignoreForAutoOpen = new Set([
        'mods_in_corpus_or_needs_review.keyword',
        'language.keyword',
      ]);

      let newOpenFacets = new Set([...openFacets]);

      Object.keys(searchFacetsValues).forEach(facet => {
        if (ignoreForAutoOpen.has(facet)) {
            return;
        }

        const normalized = facet.replace('.keyword', '').replaceAll('_', ' ');

        Object.entries(FACETS_CATEGORIES_WITH_FACETS).forEach(
            ([category, facetsInCategory]) => {
                if (facetsInCategory.includes(normalized)) {
                    newOpenFacets.add(category);
                }
            }
        );
      });

      if (datePubmedAdded || datePubmedModified || datePublished || dateCreated) {
        newOpenFacets.add('Date Range');
      }

      setOpenFacets(newOpenFacets);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <LoadingOverlay active={facetsLoading} />
            {
                Object.entries(FACETS_CATEGORIES_WITH_FACETS).map(([facetCategory, facetsInCategory]) =>
                    <div key={facetCategory} style={{textAlign: "left"}}>
                        <Button variant="light" eventkey="0" style={{textAlign: "left", whiteSpace: "nowrap", fontSize: "1.15rem", fontWeight: 500, padding: "0.375rem 0.5rem"}} onClick={() => toggleFacetGroup(facetCategory)}>
                            {openFacets.has(facetCategory) ? <IoIosArrowDropdownCircle/> : <IoIosArrowDroprightCircle/>} {facetCategory}
                        </Button>
			<Collapse in={openFacets.has(facetCategory)}>
                            <div>
				{facetCategory === 'Workflow Tags' && (
				   <>
				     {/* Advanced Workflow search entry point (SCRUM-6398): the same
				         global facet/advanced switch as Topics and Entities — the
				         query builder holds Workflow conditions alongside Topic Tags
				         so mixed criteria combine in one AND/OR tree. The workflow
				         checkboxes below stay usable in both modes and AND with the
				         builder's conditions. */}
				     <div style={{ marginLeft: '30px', marginBottom: '6px' }}>
				       <ButtonGroup size="sm">
				         <Button
				           variant={searchMode === 'facet' ? 'primary' : 'outline-secondary'}
				           onClick={() => dispatch(setSearchMode('facet'))}
				         >Facets</Button>
				         <Button
				           variant={searchMode === 'advanced' ? 'primary' : 'outline-secondary'}
				           onClick={() => dispatch(setSearchMode('advanced'))}
				         >Advanced query</Button>
				       </ButtonGroup>
				       {searchMode === 'advanced' && (
				         <div style={{ fontSize: '0.72rem', color: '#6c757d', marginTop: '2px' }}>
				           Checkbox selections below also apply, combined (AND) with the query builder.
				         </div>
				       )}
				     </div>
				   </>
				)}
				{facetCategory === 'Topics and Entities' && (
				   <>
				     {/* Advanced Topic search toggle (SCRUM-6228): sits next to the
				         Topic facet; switches between the facet panel and the query
				         builder. */}
				     <div style={{ marginLeft: '30px', marginBottom: '6px' }}>
				       <ButtonGroup size="sm">
				         <Button
				           variant={searchMode === 'facet' ? 'primary' : 'outline-secondary'}
				           onClick={() => dispatch(setSearchMode('facet'))}
				         >Facets</Button>
				         <Button
				           variant={searchMode === 'advanced' ? 'primary' : 'outline-secondary'}
				           onClick={() => dispatch(setSearchMode('advanced'))}
				         >Advanced query</Button>
				       </ButtonGroup>
				     </div>
				     {searchMode === 'facet' && (
                                       <Form.Check
					   type="checkbox"
					   label="apply selections to single tag"
					   checked={applyToSingleTag}
					   onChange={handleCheckboxChange}
					   disabled={hasGroupSEA}
					   style={{
					       display: 'inline-block',
					       marginLeft: '30px',
					       fontSize: '0.8rem',
					       opacity: hasGroupSEA ? 0.5 : 1
					   }}
                                       />
				     )}
				   </>
                                )}
                                {facetCategory === 'Date Range'
                                    ? <DateFacet facetsToInclude={facetsInCategory}/>
                                    : (facetCategory === 'Topics and Entities' && searchMode === 'advanced')
                                        ? null
                                        : <Facet facetsToInclude={facetsInCategory} renameFacets={RENAME_FACETS}/>}
                            </div>
                        </Collapse>
                    </div>
                )
            }
        </>
    )
}

export default Facets;
